import { OptionsField } from "forms/form.types";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styles from "./CardExpiryField.module.scss";
import fieldStyles from "../Field/Field.module.scss";
import validator from "validator";
import {
  numericInputHandleKeyDown,
  numericInputHandleKeyUp,
} from "../utils/ControlledNumericInputs.utils";

const CardExpiryField: React.FC<OptionsField> = ({
  id,
  name,
  value,
  placeholder,
  label,
  required,
  type,
  disabled,
  fieldCaption = "",
  errorMessage = "Favor de ingresar una fecha de expiración en formato MM/AA",
  customProps = {},
}) => {
  const [error, setError] = useState("");
  const methods = useFormContext();
  const [currentValue, setCurrentValue] = useState(value || "");
  const [ctrlPressed, setCtrlPressed] = useState(false);

  const expiryMaxLength = 6;

  const validateExpiryDate = (expiryDateValue: string) => {
    const expiryValues = expiryDateValue.split("/");
    let validParts = 0;
    expiryValues.forEach((expiryComponent) => {
      if (
        validator.isNumeric(expiryComponent) &&
        expiryComponent.length === 2
      ) {
        validParts++;
      }
    });
    if (validParts === 2) {
      setError("");
      setCurrentValue(expiryDateValue);
      return true;
    }
    setCurrentValue(expiryDateValue);
    setError(errorMessage);
    return false;
  };

  useEffect(() => {
    methods.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const activeError = error !== "";
  const emptyFieldWhenRequired =
    methods.formState.errors[name] &&
    methods.formState.errors[name].type === "required";
  const valueNotChanged = currentValue === value;

  const errorStyle =
    activeError ||
    (emptyFieldWhenRequired && (currentValue === "" || valueNotChanged))
      ? "input-error"
      : "";

  const caption = fieldCaption && (
    <span className={fieldStyles["field-caption"]}>{fieldCaption}</span>
  );
  const requiredMessage = emptyFieldWhenRequired &&
    (currentValue === "" || valueNotChanged) && (
      <span className={styles["error-text"]}>Este campo es requerido</span>
    );

  activeError &&
    (methods.formState.errors[name] = { type: "validation", message: error });

  return (
    <div className={styles.container}>
      <label>{label}</label>
      <input
        {...methods.register(name, {
          value: currentValue,
          required,
          validate: (value) => validateExpiryDate(value),
        })}
        id={id}
        value={currentValue}
        className={`${styles.input} ${styles[errorStyle]}`}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        max={`${expiryMaxLength}`}
        onKeyDown={(e) =>
          numericInputHandleKeyDown(
            e,
            currentValue,
            expiryMaxLength,
            ctrlPressed,
            setCtrlPressed
          )
        }
        onKeyUp={(e) => numericInputHandleKeyUp(e, setCtrlPressed)}
        onChange={(e) => {
          let expiryValue = e.target.value;
          if (expiryValue.length > 5) {
            return;
          }
          const currValueLength = currentValue?.length ?? 0;
          const currValue = currentValue ?? "";

          let charErased = currValue.length > expiryValue.length;
          let charAdded = currValue.length < expiryValue.length;

          if (charErased && currValue.charAt(currValueLength - 1) === "/") {
            expiryValue = currValue.slice(0, currValue.length - 2);
          } else if (charAdded && expiryValue.length === 2) {
            expiryValue = expiryValue + "/";
          }

          validateExpiryDate(expiryValue);
          setCurrentValue(expiryValue);
        }}
        {...customProps}
      />
      {caption}
      {requiredMessage}
      {error !== "" && <div className={styles["error-text"]}> {error}</div>}
    </div>
  );
};

export default CardExpiryField;
